import React, { useState, useEffect, useContext, useRef } from "react";
import { UserContext } from "../../utils/userContext";
import axios from "../../utils/axiosInstance";
import PageHeader from "../../components/ui/PageHeader";
import PageWrapper from "../../components/ui/PageWrapper";
import Button from "../../components/ui/Button";
import SubtaskEditor from "./SubtaskEditor";
import TaskEditor from "./TaskEditor";
import TaskSetEditor from "./TaskSetEditor";

/**
 * Task Registry Page - Admin-only
 * Manages reusable task templates: Subtasks, Tasks, and Task Sets
 */
const TaskRegistryPage = () => {
  const { user } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState("subtasks"); // subtasks | tasks | task-sets
  
  // Counts state
  const [subtasksCount, setSubtasksCount] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);
  const [taskSetsCount, setTaskSetsCount] = useState(0);
  
  // Subtasks state
  const [subtasks, setSubtasks] = useState([]);
  const [subtasksLoading, setSubtasksLoading] = useState(false);
  
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  
  // Task Sets state
  const [taskSets, setTaskSets] = useState([]);
  const [taskSetsLoading, setTaskSetsLoading] = useState(false);

  // Modals state
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isTaskSetModalOpen, setIsTaskSetModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Dropdown state for each item - store the open dropdown ID
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Task set dropdown and file upload
  const [taskSetDropdownOpen, setTaskSetDropdownOpen] = useState(false);
  const taskSetDropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch counts on mount
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [subtasksRes, tasksRes, taskSetsRes] = await Promise.all([
          axios.get("/registry/subtasks/count", { params: { status: "active" } }),
          axios.get("/registry/tasks/count", { params: { status: "active" } }),
          axios.get("/registry/task-sets/count", { params: { status: "all" } })
        ]);
        
        setSubtasksCount(subtasksRes.data.count || 0);
        setTasksCount(tasksRes.data.count || 0);
        setTaskSetsCount(taskSetsRes.data.count || 0);
      } catch (error) {
        console.error("Error fetching counts:", error);
      }
    };

    fetchCounts();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
      if (taskSetDropdownOpen && taskSetDropdownRef.current && !taskSetDropdownRef.current.contains(event.target)) {
        setTaskSetDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown, taskSetDropdownOpen]);

  useEffect(() => {
    if (activeTab === "subtasks") {
      fetchSubtasks();
    } else if (activeTab === "tasks") {
      fetchTasks();
    } else if (activeTab === "task-sets") {
      fetchTaskSets();
    }
  }, [activeTab]);

  const fetchSubtasks = async () => {
    setSubtasksLoading(true);
    try {
      const res = await axios.get("/registry/subtasks", {
        params: { status: "active", limit: 100 }
      });
      setSubtasks(res.data.items || []);
    } catch (error) {
      console.error("Error fetching subtasks:", error);
    } finally {
      setSubtasksLoading(false);
    }
  };

  const fetchTasks = async () => {
    setTasksLoading(true);
    try {
      const res = await axios.get("/registry/tasks", {
        params: { status: "active", limit: 100 }
      });
      setTasks(res.data.items || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchTaskSets = async () => {
    setTaskSetsLoading(true);
    try {
      const res = await axios.get("/registry/task-sets", {
        params: { status: "all", limit: 100 }
      });
      setTaskSets(res.data.items || []);
    } catch (error) {
      console.error("Error fetching task sets:", error);
    } finally {
      setTaskSetsLoading(false);
    }
  };

  const handleDeleteSubtask = async (id) => {
    if (!confirm("Archive this subtask?")) return;
    try {
      await axios.delete(`/registry/subtasks/${id}`);
      fetchSubtasks();
    } catch (error) {
      console.error("Error deleting subtask:", error);
      alert("Failed to delete subtask");
    }
  };

  const handleDeleteTask = async (id) => {
    if (!confirm("Archive this task?")) return;
    try {
      await axios.delete(`/registry/tasks/${id}`);
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      alert("Failed to delete task");
    }
  };

  const handleDeleteTaskSet = async (id) => {
    if (!confirm("Archive this task set?")) return;
    try {
      await axios.delete(`/registry/task-sets/${id}`);
      fetchTaskSets();
    } catch (error) {
      console.error("Error deleting task set:", error);
      alert("Failed to delete task set");
    }
  };

  const handlePublishTaskSet = async (id) => {
    if (!confirm("Publish this task set? This will freeze its composition.")) return;
    try {
      await axios.post(`/registry/task-sets/${id}/publish`);
      fetchTaskSets();
      alert("Task set published successfully!");
    } catch (error) {
      console.error("Error publishing task set:", error);
      alert("Failed to publish task set");
    }
  };

  const handleImportJSON = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      
      await axios.post("/registry/task-sets/import", jsonData);
      fetchTaskSets();
      alert("Task set imported successfully!");
    } catch (error) {
      console.error("Error importing task set:", error);
      if (error instanceof SyntaxError) {
        alert("Invalid JSON file");
      } else {
        alert(error.response?.data?.message || "Failed to import task set");
      }
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (!user?.isAdmin) {
    return (
      <PageWrapper>
        <p className="text-red-600">Access denied. Admin only.</p>
      </PageWrapper>
    );
  }

  return (
    <>
      <PageHeader
        title="Task Registry"
        subtitle="Manage reusable task templates"
      />
      <PageWrapper>
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("subtasks")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "subtasks"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Subtasks ({subtasksCount})
            </button>
            <button
              onClick={() => setActiveTab("tasks")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "tasks"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Tasks ({tasksCount})
            </button>
            <button
              onClick={() => setActiveTab("task-sets")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "task-sets"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Task Sets ({taskSetsCount})
            </button>
          </nav>
        </div>

        {/* Subtasks Tab */}
        {activeTab === "subtasks" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Subtasks</h2>
              <Button
                onClick={() => {
                  setEditingItem(null);
                  setIsSubtaskModalOpen(true);
                }}
                variant="solid"
              >
                New Subtask
              </Button>
            </div>

            {subtasksLoading ? (
              <p>Loading subtasks...</p>
            ) : subtasks.length === 0 ? (
              <p className="text-gray-500">No subtasks yet. Create one to get started!</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subtasks.map((subtask) => (
                  <div
                    key={subtask._id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-lg flex-1">{subtask.name}</h3>
                      <div className="relative ml-2" ref={openDropdown === subtask._id ? dropdownRef : null}>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === subtask._id ? null : subtask._id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Options"
                        >
                          <svg
                            className="w-5 h-5 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>

                        {openDropdown === subtask._id && (
                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  setEditingItem(subtask);
                                  setIsSubtaskModalOpen(true);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteSubtask(subtask._id);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                              >
                                Archive
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {subtask.description || "No description"}
                    </p>
                    {subtask.tags && subtask.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {subtask.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === "tasks" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Tasks</h2>
              <Button
                onClick={() => {
                  setEditingItem(null);
                  setIsTaskModalOpen(true);
                }}
                variant="solid"
              >
                New Task
              </Button>
            </div>

            {tasksLoading ? (
              <p>Loading tasks...</p>
            ) : tasks.length === 0 ? (
              <p className="text-gray-500">No tasks yet. Create one to get started!</p>
            ) : (
              <div className="space-y-4">
                {tasks.map((task) => (
                  <div
                    key={task._id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-2">{task.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {task.description || "No description"}
                        </p>
                        <div className="text-sm text-gray-500">
                          {task.subtaskRefs?.length || 0} subtask(s)
                        </div>
                      </div>
                      <div className="relative ml-4" ref={openDropdown === task._id ? dropdownRef : null}>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === task._id ? null : task._id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Options"
                        >
                          <svg
                            className="w-5 h-5 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>

                        {openDropdown === task._id && (
                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  setEditingItem(task);
                                  setIsTaskModalOpen(true);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteTask(task._id);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                              >
                                Archive
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Task Sets Tab */}
        {activeTab === "task-sets" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Task Sets</h2>
              <div className="relative" ref={taskSetDropdownRef}>
                <Button
                  onClick={() => setTaskSetDropdownOpen(!taskSetDropdownOpen)}
                  variant="solid"
                >
                  New Task Set
                  <svg className="w-4 h-4 ml-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                {taskSetDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setEditingItem(null);
                          setIsTaskSetModalOpen(true);
                          setTaskSetDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        Create New
                      </button>
                      <button
                        onClick={() => {
                          fileInputRef.current?.click();
                          setTaskSetDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        Upload JSON
                      </button>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImportJSON}
                  className="hidden"
                />
              </div>
            </div>

            {taskSetsLoading ? (
              <p>Loading task sets...</p>
            ) : taskSets.length === 0 ? (
              <p className="text-gray-500">No task sets yet. Create one to get started!</p>
            ) : (
              <div className="space-y-4">
                {taskSets.map((set) => (
                  <div
                    key={set._id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{set.name}</h3>
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              set.status === "published"
                                ? "bg-green-100 text-green-800"
                                : set.status === "draft"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {set.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {set.description || "No description"}
                        </p>
                        <div className="text-sm text-gray-500">
                          {set.taskRefs?.length || 0} task(s) • Version {set.version}
                          {set.publishedAt && (
                            <span className="ml-2">
                              • Published {new Date(set.publishedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="relative ml-4" ref={openDropdown === set._id ? dropdownRef : null}>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === set._id ? null : set._id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Options"
                        >
                          <svg
                            className="w-5 h-5 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>

                        {openDropdown === set._id && (
                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                            <div className="py-1">
                              {set.status === "draft" && (
                                <button
                                  onClick={() => {
                                    handlePublishTaskSet(set._id);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-gray-100 transition-colors"
                                >
                                  Publish
                                </button>
                              )}
                              {set.status !== "published" && (
                                <button
                                  onClick={() => {
                                    setEditingItem(set);
                                    setIsTaskSetModalOpen(true);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  handleDeleteTaskSet(set._id);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                              >
                                Archive
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </PageWrapper>

      {/* Modals */}
      <SubtaskEditor 
        isOpen={isSubtaskModalOpen}
        onClose={() => {
          setIsSubtaskModalOpen(false);
          setEditingItem(null);
        }}
        subtask={editingItem}
        onSaved={fetchSubtasks}
      />
      
      <TaskEditor 
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingItem(null);
        }}
        task={editingItem}
        onSaved={fetchTasks}
      />
      
      <TaskSetEditor 
        isOpen={isTaskSetModalOpen}
        onClose={() => {
          setIsTaskSetModalOpen(false);
          setEditingItem(null);
        }}
        taskSet={editingItem}
        onSaved={fetchTaskSets}
      />
    </>
  );
};

export default TaskRegistryPage;
